import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MarkdownPipe } from '../../../shared/pipes/markdown.pipe';
import { IconComponent } from '../../../shared/icon/icon';

/**
 * Renders the local-developer-environment hand-over doc inside the app.
 *
 * The markdown lives at `public/docs/local-developer-environment.md` (the
 * canonical copy is `docs/local-developer-environment.md` at the repo root — see
 * the `dev-environment-docs` skill for how the two stay in sync). We fetch it as
 * text and render it with the shared MarkdownPipe so there's no duplicated prose
 * in the component.
 */
@Component({
  selector: 'app-developer-docs',
  imports: [
    RouterLink,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MarkdownPipe,
    IconComponent,
  ],
  templateUrl: './developer-docs.html',
  styleUrl: './developer-docs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeveloperDocsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly DOC_PATH = 'docs/local-developer-environment.md';

  protected readonly content = signal<string | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(false);

    this.http
      .get(DeveloperDocsComponent.DOC_PATH, { responseType: 'text' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (text) => {
          this.content.set(text);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
  }
}
